import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {Dropdown} from 'react-bootstrap';
import {useInvoicesUIContext} from '../InvoicesUIContext';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import FilterTextField from '../../../_common/components/FilterTextField';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import {getInvoicesListIDPrefix} from '../../_redux/invoicesActions';
import * as actions from '../../_redux/invoicesActions';
import {generateExcelFile} from '../../../_common/utils/excel';
import {invoicesHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {headersInvoices} from '../../../_common/constants/exportedFilesHeaders';
import FiltersBar from '../../../_common/components/FiltersBar';
import ButtonSmartFilter from '../../../_common/components/ButtonSmartFilter';
import styles from '../../../Users/pages/users-filter/styles.module.scss';
import FilterSelect from "../../../_common/components/FilterSelect";

const prepareFilter = (queryParams, values) => {
  const {agentID, agentName, customerID, customerName, invoiceStatusID, fromDate, toDate, invoiceID, amount, type} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(agentName)) {
    filter.agentName = agentName;
  } else {
    delete newQueryParams.agentName;
  }
  if (Boolean(customerID)) {
    filter.customerID = customerID;
  } else {
    delete newQueryParams.customerID;
  }
  if (Boolean(invoiceStatusID)) {
    filter.invoiceStatusID = invoiceStatusID;
  } else {
    delete newQueryParams.invoiceStatusID;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(invoiceID)) {
    filter.invoiceID = invoiceID;
  } else {
    delete newQueryParams.invoiceID;
  }
  if (Boolean(amount)) {
    filter.amount = amount;
  } else {
    delete newQueryParams.amount;
  }
  if (Boolean(type)) {
    filter.type = type;
  } else {
    delete newQueryParams.type;
  }
  if (Boolean(customerName)) {
    filter.customerName = customerName;
  } else {
    delete newQueryParams.customerName;
  }

  return {...newQueryParams, ...filter};
};

const CustomDDItem = React.forwardRef(
  ({children, style, className, 'aria-labelledby': labeledBy}, ref) => (
    <div
      className={`${className} d-flex align-items-center pb-0 mb-0`}
      style={style}
      ref={ref}
    >
      {children}
    </div>
  )
);

export function InvoicesFilter() {
  const dispatch = useDispatch();
  const context = useInvoicesUIContext();
  const props = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams,
    };
  }, [context]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(getInvoicesListIDPrefix);
    const newQueryParams = prepareFilter(props.queryParams, values);
    if (!isEqual(newQueryParams, props.queryParams)) {
      props.setQueryParams(newQueryParams);
    }
  };

  const {formatMessage} = useIntl();
  const prefix = 'invoices-filter';
  const sfPrefix = 'invoices-smart-filter';

  const qs = new URLSearchParams(window.location.search);
  const agentID = qs.get('agentID') || '';
  const agentName = qs.get('agentName') || '';
  const customerID = qs.get('customerID') || '';
  const invoiceStatusID = qs.get('invoiceStatusID') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  const invoiceID = qs.get('invoiceID') || '';
  const amount = qs.get('amount') || '';
  const type = qs.get('type') || '';
  const timePeriodValue = moment(toDate).diff(fromDate, 'days');
  const customerName = qs.get('customerName') || '';

  const smartFilterInvoiceStatusArray = [
    {
      id: 'invoiceStatusID-all',
      invoiceStatusID: '',
      label: formatMessage({id: `${sfPrefix}-invoiceStatusID-all`, defaultMessage: 'All',})
    },
    {
      id: 'invoiceStatusID-open',
      invoiceStatusID: '1',
      label: formatMessage({id: `${sfPrefix}-invoiceStatusID-open`, defaultMessage: 'Open',})
    },
    {
      id: 'invoiceStatusID-close',
      invoiceStatusID: '2',
      label: formatMessage({id: `${sfPrefix}-invoiceStatusID-close`, defaultMessage: 'Close',})
    },
  ];
  const smartFilterDaysArray = [
    {
      id: 'all',
      timePeriod: '0',
      todayDate: '',
      pastDate: '',
      label: formatMessage({id: `${sfPrefix}-all`, defaultMessage: 'All'})
    },
    {
      id: 'lastDay',
      timePeriod: 1,
      todayDate: moment(),
      pastDate: moment().subtract(1, 'day'),
      label: formatMessage({id: `${sfPrefix}-last-day`, defaultMessage: 'Last day'})
    },
    {
      id: 'lastWeek',
      timePeriod: 7,
      todayDate: moment(),
      pastDate: moment().subtract(1, 'week'),
      label: formatMessage({id: `${sfPrefix}-last-week`, defaultMessage: 'Last week'})
    },
    {
      id: 'last30Days',
      timePeriod: 30,
      todayDate: moment(),
      pastDate: moment().subtract(30, 'days'),
      label: formatMessage({id: `${sfPrefix}-30days`, defaultMessage: '30 days'})
    },
    {
      id: 'last60Days',
      timePeriod: 60,
      todayDate: moment(),
      pastDate: moment().subtract(60, 'days'),
      label: formatMessage({id: `${sfPrefix}-60days`, defaultMessage: '60 days'})
    },
    {
      id: 'last90Days',
      timePeriod: 90,
      todayDate: moment(),
      pastDate: moment().subtract(90, 'days'),
      label: formatMessage({id: `${sfPrefix}-90days`, defaultMessage: '90 days'})
    },
  ];

  const smartFilterByInvoiceStatusID = (setFieldValue, item, handleSubmit) => {
    setFieldValue('invoiceStatusID', `${item.invoiceStatusID}`);
    handleSubmit();
  };
  const smartFilterByTimePeriod = (setFieldValue, item, handleSubmit) => {
    setFieldValue('timePeriod', `${item.timePeriod}`);
    setFieldValue('fromDate', item.pastDate);
    setFieldValue('toDate', item.todayDate);
    handleSubmit();
  };

  const exportReceiptsFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportInvoicesFileAction(props.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(data, 'Invoices.xlsx', invoicesHeader);
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        _id,
        agentId,
        agentName,
        customerID,
        customerName,
        datetime,
        amount,
        vat,
        statusName,
        reminderCount,
        receipts,
      } = item;

      const receiptsList = Boolean(receipts) && receipts.length > 0
        ? receipts.join(' ,')
        : '';

      return {
        [headersInvoices[0]]: index + 1,
        [headersInvoices[1]]: _id,
        [headersInvoices[2]]: agentId,
        [headersInvoices[3]]: agentName,
        [headersInvoices[4]]: customerID,
        [headersInvoices[5]]: customerName,
        [headersInvoices[6]]: Boolean(datetime)
          ? moment(datetime).format('DD.MM.YYYY') : '',
        [headersInvoices[7]]: amount ? parseFloat(amount.toFixed(2)) : '',
        [headersInvoices[8]]: vat === 0 ? 'אין מע”מ' : 'כולל מע”מ',
        [headersInvoices[9]]: statusName,
        [headersInvoices[10]]: reminderCount,
        [headersInvoices[11]]: receiptsList,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        agentID,
        agentName,
        customerID,
        invoiceStatusID,
        fromDate,
        toDate,
        invoiceID,
        amount,
        type,
        timePeriod: timePeriodValue || '0',
        customerName
      }}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterTextField
                fieldName="agentID"
                placeholder={formatMessage({id: `${prefix}-agentID`, defaultMessage: 'Agent Id'})}
                value={values.agentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentName"
                placeholder={formatMessage({id: `${prefix}-agentName`, defaultMessage: 'Agent name'})}
                value={values.agentName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerID"
                placeholder={formatMessage({id: `${prefix}-customerID`, defaultMessage: 'Customer Id'})}
                value={values.customerID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerName"
                placeholder={formatMessage({id: `${prefix}-customerName`, defaultMessage: 'Customer name'})}
                value={values.customerName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterFromDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="invoiceID"
                placeholder={formatMessage({id: `${prefix}-invoiceID`, defaultMessage: 'Invoise Id'})}
                value={values.invoiceID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName={'type'}
                valueName={values.type}
                options={context?.invoiceTypes}
                placeholder={formatMessage({id: `${prefix}-type`, defaultMessage: 'Type'})}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="amount"
                placeholder={formatMessage({id: `${prefix}-amount`, defaultMessage: 'Amount'})}
                value={values.amount}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <div className="d-flex align-items-center justify-content-between">
              <ButtonSmartFilter
                header={formatMessage({id: `${sfPrefix}-header`, defaultMessage: 'Smart filter'})}
              >
                <Dropdown.Item as={CustomDDItem}>
                  <div className="d-flex">
                    <h7 className={styles.filterName}>
                      {formatMessage({
                        id: `${sfPrefix}-invoiceStatusID-header`,
                        defaultMessage: 'Invoice status ID',
                      })}
                    </h7>
                    <div className="d-flex flex-nowrap">
                      {smartFilterInvoiceStatusArray.map((item) => {
                        return (
                          <div className={`${sfPrefix}-item`}>
                            <input
                              type="radio"
                              className="hide-checkbox"
                              id={item.id}
                              name="invoiceStatusID"
                              onBlur={formikProps.handleBlur}
                              checked={values.invoiceStatusID === `${item.invoiceStatusID}`}
                              onChange={() => {
                                smartFilterByInvoiceStatusID(formikProps.setFieldValue, item, formikProps.handleSubmit);
                              }}
                            />
                            <label htmlFor={item.id} className="custom-checkbox mr-8">
                              {item.label}
                            </label>
                          </div>
                        );
                      })
                      }
                    </div>
                  </div>
                </Dropdown.Item>
                <Dropdown.Item as={CustomDDItem}>
                  <div className="d-flex">
                    <h7 className={styles.filterName}>
                      {formatMessage({
                        id: `${sfPrefix}-time-period-header`,
                        defaultMessage: 'Invoice time period',
                      })}
                    </h7>
                    <div className="d-flex flex-wrap">
                      <div className="d-flex flex-wrap w-100">
                        {smartFilterDaysArray.map((item) => {
                          return (
                            <div className={`${sfPrefix}-time-item`}>
                              <input
                                type="radio"
                                className="hide-checkbox"
                                id={item.id}
                                name="timePeriod"
                                onBlur={formikProps.handleBlur}
                                checked={values.timePeriod === item.timePeriod}
                                onChange={() => {
                                  smartFilterByTimePeriod(formikProps.setFieldValue, item, formikProps.handleSubmit);
                                }}
                              />
                              <label htmlFor={item.id} className="custom-checkbox mr-4">
                                {item.label}
                              </label>
                            </div>
                          );
                        })
                        }
                      </div>
                    </div>
                  </div>
                </Dropdown.Item>
              </ButtonSmartFilter>
              <ButtonExportToExcel
                loading={loading}
                exportFunc={exportReceiptsFileFunc}
              />
            </div>
          }
        />
      )}
    </Formik>
  );
}